import { EntryMethod } from 'src/common/enumerations/entry-method';
import XLRLogger from 'src/common/helpers/logger';
import { store } from 'src/common/redux/configure-store';
import { PinPadActions } from 'src/common/redux/reducers/pinpad-reducer';
import { handleCustomerAddFromPinPad } from 'src/customer-loyalty/business-logic/set-customer';
import {
  beginPaymentSession,
  cancelPinpadOperation,
  getCardDetailsFromPinPad,
  inPaymentSession,
  promptPhoneNumber,
} from 'src/devices/payment/pinpad-operations';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import { EpsData } from './dtos/eps-data';
import { enableCardEntry } from './pinpad-operations';
import { PmiStatusCodes } from './pmi-status-codes-enums';

let processingCardDetails = false;
let hasCardDetails = false;
let cardDetails: EpsData | undefined = undefined;
let cardDetailsPromise: Promise<EpsData | undefined> | undefined;
let processingCustomer: boolean = false;
let cancelled: boolean = false;
let enableCardEntryPerformed: boolean = false;

export const setCancelled = (cancel: boolean) => {
  cancelled = cancel;
};

export const setEnableCardEntryPerformed = (value: boolean) => {
  enableCardEntryPerformed = value;
};

/**
 * Swipe ahead will start processing an EFT transaction in the background.
 * This function should be called when ready for a customer to swipe and with
 * the current amounts due
 * @param tenderAmount The amount to tender for this swipe
 * @param totalAmountDue The total amount due for the whole transaction
 * @param entryMethod Optional: The desired entry method, typically used for manual entry
 */
export const swipeAhead = async (getAltid = false): Promise<void> => {
  cancelled = false;

  await handleGetAltId(getAltid);
  if (!processingCardDetails && !cancelled) {
    processingCardDetails = true;

    await enableCardEntry().then(async () => {
      if (!inPaymentSession()) {
        await beginPaymentSession();
      }
      const amountDue = store.getState().itemList.amountDue;
      waitForCardDetails(amountDue, amountDue).catch(error =>
        XLRLogger.error({
          data: error,
          message: 'Error while getting data from a swiped card',
          source: 'get-card-details.ts',
        }),
      );
    });
  }
  cancelled = false;
};

const handleGetAltId = async (getAltid: boolean): Promise<void> => {
  if (
    getAltid &&
    !store.getState().transaction.transactionCustomer &&
    !store.getState().pinPad.hasCardData &&
    !processingCustomer
  ) {
    processingCustomer = true;
    return cancelPinpadOperation().then(() => {
      const { shouldPromptPhoneNumberWhenSwipeAhead } = getCurrentXLRConfig();
      if (shouldPromptPhoneNumberWhenSwipeAhead) {
        cancelled = false;
        return promptPhoneNumber()
          .then(handleCustomerAddFromPinPad)
          .catch(err => {
            if (err.statusCode !== PmiStatusCodes.Cancelled) {
              popupMessage(err);
            }
          })
          .finally(() => {
            processingCustomer = false;
          });
      }
    });
  }
};

let waiting = false;

/**
 * A function that will return a promise containing the card details. In the case of a swipe ahead,
 * this could resolve immediately with the card data, otherwise it will wait out the card entry.
 * @param tenderedAmount The amount to tender with this card
 * @param totalAmountDue The total amount due for the transaction
 * @param entryMethod Optional: The desired entry method, typically used for manual entry
 * @returns A promise that will resolve with the card details as EPSData
 */
export const waitForCardDetails = async (
  tenderedAmount: number,
  totalAmountDue: number,
  entryMethod?: EntryMethod,
): Promise<EpsData | undefined> => {
  if (!inPaymentSession()) {
    await beginPaymentSession();
  }
  if (waiting && cardDetailsPromise) {
    return cardDetailsPromise;
  }

  waiting = true;
  cancelled = false;

  cardDetailsPromise = getCardDetailsFromPinPad(tenderedAmount, totalAmountDue, entryMethod)
    .then(_cardDetails => {
      cardDetails = _cardDetails;
      hasCardDetails = true;
      store.dispatch(PinPadActions.setHasCardData(true));
      return _cardDetails;
    })
    .catch(error => {
      if (cancelled && error.statusCode === PmiStatusCodes.Cancelled) {
        return undefined;
      }
      XLRLogger.error({
        message: 'Error getting card details',
        source: 'get-card-details.ts',
        data: error,
      });
      throw error;
    })
    .finally(() => {
      waiting = false;
      processingCardDetails = false;
      cardDetailsPromise = undefined;
    });

  return cardDetailsPromise;
};

/**
 * A function to check if we currently have any card data
 * @returns A value representing if the POS currently has card data from the PIN Pad
 */
export const hasCardData = (): boolean => {
  return hasCardDetails;
};

/**
 * A function to get the card data from the PIN Pad.
 * @returns The card data from the PIN Pad
 */
export const getCardData = (): EpsData => {
  if (cardDetails) {
    return cardDetails;
  } else {
    throw new Error('Missing card data');
  }
};
/**
 * A function to check if we called enableCardEntry yet
 * @returns A value representing if the POS has called enableCardEntry yet
 */
export const isEnableCardEntryPerformed = (): boolean => {
  return enableCardEntryPerformed;
};

/**
 * A function to reset the card data from the PIN Pad so a new financial transaction
 * may begin
 */
export const resetCardData = (): void => {
  hasCardDetails = false;
  cardDetails = undefined;
  processingCardDetails = false;
  store.dispatch(PinPadActions.setHasCardData(false));
  setEnableCardEntryPerformed(false);
};

/**
 * A function to reset the waiting and cardPromise so manual entry
 * may begin
 */
export const resetForManualEntry = (): void => {
  waiting = false;
  cardDetailsPromise = undefined;
};

export const resetCardWaiting = (): void => {
  waiting = false;
  cardDetailsPromise = undefined;
  processingCardDetails = false;
};
